import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { ScrollText, Plus, Save, Trash2, Star, Loader2, Search } from 'lucide-react';
import api from '../../utils/api';
import usePermission from '../../hooks/usePermission';

// Terms & Conditions for quotations. Several sets can be kept (Standard, Export, Installation …);
// the default set fills every new quotation, and any set can be loaded on the quotation form.
const blank = () => ({ id: null, name: '', content: '', is_default: false, is_active: true });

export default function TermsConditionsPage() {
  const { canWrite, canEdit, canDelete } = usePermission('terms');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState(null);           // the set being viewed / edited
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState('');

  const load = useCallback(async (selectId) => {
    setLoading(true);
    try {
      const r = await api.get('/quotation-terms');
      const rows = r.data || [];
      setList(rows);
      const pick = rows.find(x => x.id === selectId) || rows.find(x => x.is_default) || rows[0];
      setF(pick ? { ...pick, is_default: !!pick.is_default, is_active: !!pick.is_active } : null);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to load'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const canSave = f && (f.id ? canEdit : canWrite);
  const save = async () => {
    if (!String(f.name || '').trim()) return toast.error('Enter a name for these terms');
    if (!String(f.content || '').trim()) return toast.error('Write the terms and conditions');
    setSaving(true);
    try {
      const body = { name: f.name, content: f.content, is_default: f.is_default, is_active: f.is_active };
      const r = f.id ? await api.put(`/quotation-terms/${f.id}`, body) : await api.post('/quotation-terms', body);
      toast.success(r.data.message);
      load(r.data.id);
    } catch (err) { toast.error(err.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };
  const del = async () => {
    if (!window.confirm(`Delete the terms "${f.name}"? Quotations already made keep their own copy.`)) return;
    try { const r = await api.delete(`/quotation-terms/${f.id}`); toast.success(r.data.message); load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Delete failed'); }
  };
  const makeDefault = async t => {
    try { await api.put(`/quotation-terms/${t.id}`, { name: t.name, content: t.content, is_default: true, is_active: true }); toast.success(`"${t.name}" is now the default`); load(t.id); }
    catch (err) { toast.error(err.response?.data?.message || 'Could not change the default'); }
  };
  const shown = list.filter(t => `${t.name} ${t.content}`.toLowerCase().includes(q.trim().toLowerCase()));
  const lines = f ? String(f.content || '').split('\n').filter(l => l.trim()).length : 0;

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-6xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-800 flex items-center gap-2"><ScrollText className="w-6 h-6 text-blue-600" /> Terms &amp; Conditions</h1>
          <p className="text-sm text-gray-500">Set the terms printed on quotations. The default set fills every new quotation; any set can be loaded on the quotation form.</p>
        </div>
        {canWrite && <button onClick={() => setF(blank())} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold"><Plus className="w-4 h-4" /> New Terms</button>}
      </div>

      <div className="grid gap-4 md:grid-cols-[280px_minmax(0,1fr)]">
        {/* Saved sets */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden self-start">
          <div className="p-2 border-b border-gray-100">
            <span className="flex items-center gap-1.5 border border-gray-200 rounded-lg px-2.5 py-1.5">
              <Search className="w-4 h-4 text-gray-400" />
              <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search terms" className="flex-1 min-w-0 text-sm outline-none" />
            </span>
          </div>
          {loading ? <p className="p-6 text-center text-sm text-gray-400">Loading…</p>
            : !shown.length ? <p className="p-6 text-center text-sm text-gray-400">{list.length ? 'No match' : `No terms yet${canWrite ? ' — click New Terms' : ''}`}</p>
            : (
              <div className="divide-y divide-gray-100">
                {shown.map(t => (
                  <button key={t.id} onClick={() => setF({ ...t, is_default: !!t.is_default, is_active: !!t.is_active })}
                    className={`w-full text-left px-3 py-2.5 hover:bg-gray-50 ${f?.id === t.id ? 'bg-blue-50' : ''}`}>
                    <div className="flex items-center gap-1.5">
                      <span className={`font-semibold text-sm ${t.is_active ? 'text-gray-800' : 'text-gray-400'}`}>{t.name}</span>
                      {!!t.is_default && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800">DEFAULT</span>}
                      {!t.is_active && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-gray-100 text-gray-500">Inactive</span>}
                    </div>
                    <div className="text-xs text-gray-400 truncate">{String(t.content || '').split('\n')[0]}</div>
                  </button>
                ))}
              </div>
            )}
        </div>

        {/* Editor */}
        {f ? (
          <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
            <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] items-end">
              <label className="text-xs font-semibold text-gray-500">Name *
                <input className="tinp mt-1" maxLength={150} disabled={!canSave} value={f.name} onChange={e => setF({ ...f, name: e.target.value })} placeholder="e.g. Standard Terms, Export Order, With Installation" /></label>
              <div className="flex flex-wrap items-center gap-3 pb-2">
                <label className="flex items-center gap-1.5 text-sm text-gray-700"><input type="checkbox" disabled={!canSave} checked={!!f.is_default} onChange={e => setF({ ...f, is_default: e.target.checked, is_active: e.target.checked ? true : f.is_active })} /> Default for new quotations</label>
                <label className="flex items-center gap-1.5 text-sm text-gray-700"><input type="checkbox" disabled={!canSave || f.is_default} checked={!!f.is_active} onChange={e => setF({ ...f, is_active: e.target.checked })} /> Active</label>
              </div>
            </div>
            <label className="text-xs font-semibold text-gray-500 block">Terms &amp; Conditions *
              <textarea className="tinp mt-1 font-mono" rows={16} disabled={!canSave} value={f.content} onChange={e => setF({ ...f, content: e.target.value })}
                placeholder={'1. Prices are valid for 30 days from the quotation date.\n2. 50% advance with the order, balance before dispatch.\n3. Delivery: 4–6 weeks from the advance and approved drawings.\n4. Transport and unloading as mentioned in the quotation.\n5. Prices are subject to GST as applicable.'} /></label>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-xs text-gray-400">{lines} line{lines === 1 ? '' : 's'} · printed exactly as typed, line breaks included{f.updated_by_name ? ` · last changed by ${f.updated_by_name}` : ''}</span>
              <div className="flex flex-wrap gap-2">
                {f.id && !f.is_default && canEdit && f.is_active && <button onClick={() => makeDefault(f)} className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 text-sm font-semibold"><Star className="w-4 h-4" /> Make Default</button>}
                {f.id && !f.is_default && canDelete && <button onClick={del} className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-red-200 text-red-600 text-sm font-semibold hover:bg-red-50"><Trash2 className="w-4 h-4" /> Delete</button>}
                {canSave && <button onClick={save} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold disabled:opacity-50">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save</button>}
              </div>
            </div>
          </div>
        ) : !loading && (
          <div className="bg-white rounded-xl border border-dashed border-gray-200 p-10 text-center text-sm text-gray-400">
            No terms yet.{canWrite ? ' Click New Terms to write your first set — it becomes the default for new quotations.' : ''}
          </div>
        )}
      </div>
      <style>{`.tinp{display:block;width:100%;padding:8px 10px;border:1px solid #d1d5db;border-radius:8px;font-size:13px;font-weight:400;color:#111827;background:#fff;line-height:1.55} .tinp:disabled{background:#f9fafb;color:#4b5563} .tinp:focus{outline:2px solid #bfdbfe;border-color:#3b82f6}`}</style>
    </div>
  );
}
